import { runtimeConfig } from "config/runtimeConfig";
import React, { useState } from 'react';
import { CSSTransition } from "react-transition-group";
import RulesLogs from "./RulesLog";
import ServiceWrapper from "Services/ServiceWrapper";
import AuthManager from "Common/AuthManager";
import dayjs from "utils/dayjs";
import SidePanel from "Components/DesignSystem/SidePanel/SidePanel";
import Table from "Components/DesignSystem/Table/Table";
import Button from "Components/DesignSystem/Button/Button";
import Status from "Components/DesignSystem/Status/Status";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Cross, Copy, Refresh } from "Components/DesignSystem/Iconography/Icons";
import { toast } from "Components/DesignSystem/Toast/useToast";

const API_URL = runtimeConfig.GMS_BACKEND_API_URL;

/**
 * The Rule Action Queue drawer on the DS `SidePanel` + `Table` (§ 109).
 *
 * Two pinned behaviours make this table different from the Compatibility grid, and
 * both are why § 106's sticky work had to land first:
 *
 * - **The NAME column stays put.** Seven columns in a 66%-wide drawer scroll
 *   horizontally, and the name (plus the uid under it) is the only thing identifying
 *   which of several identically-named rules a row is — the real queue routinely holds
 *   four "sabin test rule" entries differing only by uid.
 * - **The header row stays put.** The legacy table did this with
 *   `<thead className="sticky-header-table">` — a GLOBAL class with two divergent
 *   copies under `src/`, both hardcoding `background: #dfe1e6`, and both applying to
 *   every table in the application. `stickyHeader` + `maxHeight` do it locally.
 *
 * **The status chip: a fixed crash.** The tones came from a hand-built map
 *
 *   `{ Completed: [...], "In Progress": [...], "In Queue": [...], Failed: [...] }`
 *
 * read as `colorObj[queueItem.status][0]` — with no fallback. Any status outside those
 * four threw `Cannot read properties of undefined (reading '0')` **during render**,
 * taking down the whole panel rather than degrading one cell. The queue's status
 * strings come from the backend, so this was a live crash waiting on a new enum value.
 * The suite pinned it as `must-not-regress` — deliberately, to stop it being *silently*
 * corrected — so the pin is updated in the same change with the reason, which is what
 * that convention is for. An unknown status now renders `neutral` and shows its own
 * text, so an unexpected value is visible and diagnosable instead of fatal.
 *
 * The chip was also a `<button>` that did nothing when pressed: focusable, in the tab
 * order, announced as a control, and wired to no handler. `Status` is not a control.
 *
 * Behaviour deliberately preserved exactly: the refresh button sets `loading` and only
 * calls `fetchRulesQueue` 2 seconds later via a bare `setTimeout` (not a request); the
 * close button calls `setShowRulesQueue()` with **no argument** (the parent passes its
 * raw `useState` setter, so this sets `undefined` — falsy — and the panel hides);
 * `handleRetry` GETs `rule_queue/{uid}/retry` and reports failure with the copy-pasted
 * "Log fetch failed." message; Retry appears only for `Failed`; and the copy icon
 * copies `queueItem.id`, not the `uid` displayed beside it.
 */
const RulesQueue = (props) => {
  const { setShowRulesQueue, rulesQueue, fetchRulesQueue } = props;

  const dateFormatter = (timestamp) => {
    const dateTime = dayjs.unix(timestamp).utc();
    const formattedDateTime = dateTime.format("DD-MMM-YYYY, hh:mm A [UTC]");
    return formattedDateTime;
  };

  /**
   * Status → tone. Replaces the hex pairs in `colorObj`, whose greens and ambers
   * (`#E3FCEF`/`#006644`, `#FFF7DB`/`#DD7800`) were near-misses for palette values.
   * Unlike `colorObj`, a lookup miss here is not fatal — see `toneFor`.
   */
  const STATUS_TONE = {
    Completed: "success",
    "In Progress": "warning",
    "In Queue": "info",
    Failed: "danger",
  };

  /** An unknown status is a surprise, not a crash: show it, tinted neutral. */
  const toneFor = (status) => STATUS_TONE[status] ?? "neutral";

  const [viewLog, setViewLog] = useState(false);
  const [logDataIndex, setLogDataIndex] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const handleViewLog = (index) => {
    setViewLog(true);
    setLogDataIndex(index);
  };

  const handleRetry = async (index) => {
    setLoading(true);
    const res = await ServiceWrapper.doGet(
      `${API_URL}rule_queue/${index}/retry`,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (res?.data?.done) {
      toast.success("Successfully triggered retry mechanism");
    } else {
      toast.error("Log fetch failed.");
    }

    setLoading(false);
  };

  function handleClipbord(id) {
    navigator.clipboard.writeText(id).then(() => toast.success("UID copied"));
  }

  function copy(id, event) {
    event.stopPropagation();
    handleClipbord(id);
  }

  const columns = [
    {
      id: "name",
      header: "Name",
      accessor: (r) => r?.rule_name ?? "",
      sticky: "left",
      minWidth: "260px",
      render: (_value, row, rowIndex) => (
        <div data-testid={`rules-rulesqueue-div-1-${rowIndex}`}>
          <Typography variant="body-primary" as="div">
            {row?.rule_name}
          </Typography>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
              {row?.uid}
            </Typography>
            {row?.showCopyIcon && (
              <Button
                variant="subtle"
                size="sm"
                aria-label={`Copy id for ${row?.rule_name ?? "rule"}`}
                data-testid={`rules-rulesqueue-i-2-${rowIndex}`}
                onClick={(event) => copy(row?.id, event)}
              >
                <Copy />
              </Button>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "action",
      header: "Rule Action",
      accessor: (r) => r?.action ?? "",
      minWidth: "110px",
      render: (_value, row, rowIndex) => (
        <span data-testid={`rules-rulesqueue-td-3-${rowIndex}`}>{row?.action}</span>
      ),
    },
    {
      id: "status",
      header: "Commit Status",
      accessor: (r) => r?.status ?? "",
      minWidth: "140px",
      render: (_value, row, rowIndex) => (
        <Status
          tone={toneFor(row?.status)}
          label={row?.status}
          data-testid={`rules-rulesqueue-button-2-${rowIndex}`}
        />
      ),
    },
    {
      id: "created_at",
      header: "Last Updated",
      accessor: (r) => dateFormatter(r?.created_at),
      cellType: "meta",
      minWidth: "200px",
      render: (value, _row, rowIndex) => (
        <span data-testid={`rules-rulesqueue-td-4-${rowIndex}`}>{value}</span>
      ),
    },
    {
      id: "edge_count",
      header: "Edge Count",
      accessor: (r) => r?.edge_count,
      cellType: "numerical",
      minWidth: "110px",
      render: (_value, row, rowIndex) => (
        <span data-testid={`rules-rulesqueue-td-5-${rowIndex}`}>
          {row?.edge_count ? row.edge_count : ""}
        </span>
      ),
    },
    {
      id: "log",
      header: "Log",
      accessor: () => null,
      /* 120px, not 100px (§ 111). "View Log" is ~62px at 14px, plus the button's own
         inset and the cell's 12px padding either side — at 100px the label wrapped to
         two lines in EVERY row, which is what the column looked like in production. The
         `whitespace-nowrap` below is the actual guarantee; the width stops the nowrap
         from forcing a horizontal scrollbar to pay for it. */
      minWidth: "120px",
      render: (_value, row, rowIndex) => (
        <span data-testid={`rules-rulesqueue-td-6-${rowIndex}`}>
          {/* Was a `<Link to="#">`: a navigation element that navigated nowhere and
              opened a drawer via onClick. */}
          <Button
            variant="subtle"
            size="sm"
            className="tw-whitespace-nowrap"
            aria-label={`View log for ${row?.rule_name ?? "rule"}`}
            data-testid={`rules-queue-view-log-${rowIndex}`}
            onClick={() => handleViewLog(rowIndex)}
          >
            View Log
          </Button>
        </span>
      ),
    },
    {
      id: "retry",
      header: "",
      accessor: () => null,
      align: "right",
      width: "90px",
      render: (_value, row, rowIndex) =>
        row?.status === "Failed" ? (
          <Button
            variant="subtle"
            size="sm"
            className="tw-whitespace-nowrap"
            aria-label={`Retry ${row?.rule_name ?? "rule"}`}
            data-testid={`rules-queue-retry-${rowIndex}`}
            onClick={() => handleRetry(rulesQueue[rowIndex]?.uid)}
          >
            Retry
          </Button>
        ) : null,
    },
  ];

  return (
    <SidePanel
      isOpen
      onClose={() => setShowRulesQueue()}
      width="xl"
      ariaLabel="Rules Queue"
      title={
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <span>Rules Queue</span>
          <Button
            variant="subtle"
            size="sm"
            aria-label="Refresh rules queue"
            data-testid="rules-queue-refresh-btn"
            onClick={() => {
              setLoading(true);
              setTimeout(() => {
                fetchRulesQueue();
                setLoading(false);
              }, 2000);
            }}
          >
            {/* DS Refresh (currentColor) — GmsIcons' ReloadMediumIcon bakes
                stroke #091CAA, which vanishes on the dark ground. Spin-while-
                loading mirrors the Rules Log drawer, this header's stated pair. */}
            <Refresh className={loading ? "tw-animate-spin" : undefined} />
          </Button>
        </span>
      }
      tabs={[]}
      /* Required: without `headerActions`, SidePanel falls back to its legacy
         Cancel/Save pair (§ 53) and this drawer has nothing to save. The testid and
         the no-argument `setShowRulesQueue()` call are both the legacy X button's. */
      headerActions={
        <Button
          variant="subtle"
          size="sm"
          aria-label="Close rules queue"
          data-testid="rules-rulesqueue-button-1"
          onClick={() => setShowRulesQueue()}
        >
          <Cross />
        </Button>
      }
      data-testid="rules-queue-panel"
    >
      <Table
        columns={columns}
        data={rulesQueue ?? []}
        rowKey="uid"
        striped
        stickyHeader
        maxHeight="75vh"
        loading={loading}
        data-testid="rules-queue-grid"
      />
      {viewLog && rulesQueue[logDataIndex] && (
        <CSSTransition
          timeout={1000}
          classNames={{
            enterActive: "animate__animated animate__flipInX",
            exitActive: "animate__animated animate__flipOutX",
          }}
        >
          <RulesLogs
            logData={rulesQueue[logDataIndex].logs}
            logId={rulesQueue[logDataIndex].uid}
            setViewLog={setViewLog}
          />
        </CSSTransition>
      )}
    </SidePanel>
  );
};

export default RulesQueue;
